import React from 'react';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { PalmTree, BushyTree } from './VenueDecorations';

/**
 * Renders admin-placed decorations on the venue.
 * Types: tree, bush, palm, lantern, bench, flag, sign, banner, sculpture, custom_icon
 */
export function PropRenderer({ prop }) {
  const scale = prop.scale || 1;
  const pos = [prop.position_x || 0, prop.position_y || 0, prop.position_z || 0];
  const rot = [0, ((prop.rotation || 0) * Math.PI) / 180, 0];
  const color = prop.color || null;

  switch (prop.type) {
    case 'tree':
    case 'bush':
      return <BushyTree position={pos} scale={scale * (prop.type === 'bush' ? 0.5 : 1)} />;
    case 'palm':
      return <PalmTree position={pos} scale={scale} />;
    case 'lantern':
      return (
        <group position={pos} rotation={rot} scale={scale}>
          <mesh position={[0, 0.6, 0]} castShadow>
            <cylinderGeometry args={[0.15, 0.2, 1.2, 6]} />
            <meshStandardMaterial color="#404040" metalness={0.5} />
          </mesh>
          <mesh position={[0, 1.35, 0]}>
            <sphereGeometry args={[0.3, 10, 10]} />
            <meshStandardMaterial color={color || '#fbbf24'} emissive={color || '#fbbf24'} emissiveIntensity={1.3} />
          </mesh>
          <pointLight position={[0, 1.35, 0]} intensity={0.55} color={color || '#fbbf24'} distance={5} />
        </group>
      );
    case 'bench':
      return (
        <group position={pos} rotation={rot} scale={scale}>
          {/* Seat */}
          <mesh position={[0, 0.5, 0]} castShadow>
            <boxGeometry args={[2.2, 0.1, 0.5]} />
            <meshStandardMaterial color={color || '#7c5e3c'} roughness={0.7} />
          </mesh>
          {/* Backrest */}
          <mesh position={[0, 0.85, -0.2]} castShadow>
            <boxGeometry args={[2.2, 0.7, 0.08]} />
            <meshStandardMaterial color={color || '#7c5e3c'} />
          </mesh>
          {/* Legs */}
          {[[-0.9, 0.25], [0.9, 0.25]].map(([x]) => (
            <mesh key={`leg-${x}`} position={[x, 0.25, 0]} castShadow>
              <boxGeometry args={[0.08, 0.5, 0.5]} />
              <meshStandardMaterial color="#4b5563" metalness={0.5} />
            </mesh>
          ))}
        </group>
      );
    case 'flag':
      return (
        <group position={pos} rotation={rot} scale={scale}>
          <mesh position={[0, 3, 0]} castShadow>
            <cylinderGeometry args={[0.08, 0.08, 6, 6]} />
            <meshStandardMaterial color="#a0a0a0" metalness={0.6} />
          </mesh>
          <mesh position={[1, 5, 0]} castShadow>
            <planeGeometry args={[2, 1.2]} />
            <meshStandardMaterial color={color || '#dc2626'} side={THREE.DoubleSide} />
          </mesh>
        </group>
      );
    case 'sign':
      return (
        <group position={pos} rotation={rot} scale={scale}>
          <mesh position={[0, 1.5, 0]} castShadow>
            <cylinderGeometry args={[0.08, 0.08, 3, 6]} />
            <meshStandardMaterial color="#4b5563" metalness={0.6} />
          </mesh>
          <mesh position={[0, 3, 0]} castShadow>
            <boxGeometry args={[2, 1, 0.1]} />
            <meshStandardMaterial color={color || '#f59e0b'} emissive={color || '#f59e0b'} emissiveIntensity={0.3} />
          </mesh>
          {prop.label && (
            <Html zIndexRange={[10, 0]} position={[0, 3, 0.06]} center distanceFactor={18} occlude={false}>
              <div className="text-slate-900 font-black text-[9px] tracking-wider px-1 whitespace-nowrap pointer-events-none select-none">
                {prop.label}
              </div>
            </Html>
          )}
        </group>
      );
    case 'banner':
      return (
        <group position={pos} rotation={rot} scale={scale}>
          <mesh position={[0, 3, 0]} castShadow>
            <cylinderGeometry args={[0.1, 0.1, 6, 6]} />
            <meshStandardMaterial color="#a0a0a0" />
          </mesh>
          <mesh position={[0, 3, 0.1]} castShadow>
            <planeGeometry args={[1.2, 4]} />
            <meshStandardMaterial color={color || '#22c55e'} side={THREE.DoubleSide} />
          </mesh>
          {prop.label && (
            <Html zIndexRange={[10, 0]} position={[0, 3, 0.15]} center distanceFactor={16} occlude={false}>
              <div className="text-white font-black text-[10px] tracking-wider pointer-events-none select-none" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>
                {prop.label}
              </div>
            </Html>
          )}
        </group>
      );
    case 'sculpture':
      // Green leaf sculpture
      return (
        <group position={pos} rotation={rot} scale={scale}>
          <mesh position={[0, 1.5, 0]} castShadow>
            <coneGeometry args={[1.2, 3, 6, 1, false, 0, Math.PI * 1.5]} />
            <meshStandardMaterial color={color || '#16a34a'} roughness={0.9} side={THREE.DoubleSide} />
          </mesh>
          <mesh position={[0.6, 2, 0.3]} castShadow>
            <coneGeometry args={[0.8, 2, 5, 1, false, 0, Math.PI * 1.5]} />
            <meshStandardMaterial color={color || '#15803d'} roughness={0.9} side={THREE.DoubleSide} />
          </mesh>
        </group>
      );
    case 'custom_icon':
      return (
        <group position={pos} rotation={rot} scale={scale}>
          <mesh position={[0, 1.5, 0]} castShadow>
            <cylinderGeometry args={[0.08, 0.08, 3, 6]} />
            <meshStandardMaterial color="#4b5563" metalness={0.6} />
          </mesh>
          <mesh position={[0, 3.2, 0]} castShadow>
            <cylinderGeometry args={[0.9, 0.9, 0.2, 24]} />
            <meshStandardMaterial color={color || '#3b82f6'} emissive={color || '#3b82f6'} emissiveIntensity={0.5} />
          </mesh>
          <Html zIndexRange={[10, 0]} position={[0, 3.2, 0.15]} center distanceFactor={16} occlude={false}>
            <div className="text-3xl pointer-events-none select-none" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }}>
              {prop.icon || '📍'}
            </div>
          </Html>
          {prop.label && (
            <Html zIndexRange={[10, 0]} position={[0, 4.2, 0]} center distanceFactor={16} occlude={false}>
              <div className="bg-slate-900/85 text-white text-[9px] font-bold px-2 py-0.5 rounded pointer-events-none select-none whitespace-nowrap">
                {prop.label}
              </div>
            </Html>
          )}
        </group>
      );
    default:
      return null;
  }
}
